import { sendResetPasswordEmail } from '@/api/send-reset-password-email.ts'
import { Button } from '@/components/ui/button.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { emailValidator } from '@/utils/validators.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { CheckCircle, Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

export const Route = createFileRoute('/auth/password/send-reset-email')({
  component: SendResetPasswordEmail,
  head: () => ({
    meta: [
      {
        title: 'Recuperar conta | Okami',
      },
    ],
  }),
})

const formSchema = z.object({
  email: emailValidator,
})

type FormSchema = z.infer<typeof formSchema>

export function SendResetPasswordEmail() {
  const navigate = useNavigate()
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  })

  const {
    mutate: sendEmail,
    isSuccess,
    isPending,
  } = useMutation({
    mutationKey: ['send-reset-password-email'],
    mutationFn: sendResetPasswordEmail,
    onSuccess() {
      toast.info('E-mail enviado com sucesso, verifique sua caixa de entrada')

      void navigate({ to: '/auth/sign-in', replace: true })
    },
    onError() {
      toast.error('Ocorreu um erro ao enviar o e-mail, tente novamente mais tarde')
    },
  })

  return (
    <div className="p-8">
      <div className="flex w-[320px] flex-col justify-center gap-6">
        {isSuccess ? (
          <div className="p-8">
            <div className="flex w-[320px] flex-col justify-center gap-6">
              <header className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-3xl font-semibold tracking-tight">Enviado</h1>

                <CheckCircle className="size-16 text-primary" />

                <p className="text-sm text-muted-foreground">
                  Se os dados informados estiverem corretos, você receberá um e-mail com as instruções para
                  recuperar o acesso
                </p>
              </header>

              <Button variant="link" onClick={() => navigate({ to: '/auth/sign-in', replace: true })}>
                Voltar para o Login
              </Button>
            </div>
          </div>
        ) : (
          <>
            <header className="flex flex-col gap-2 text-center">
              <h1 className="text-2xl font-semibold tracking-tight">Recuperar acesso</h1>
              <p className="text-sm text-muted-foreground">
                Se você esqueceu sua senha, informe seu e-mail para recuperar o acesso
              </p>
            </header>
            <form className="flex flex-col gap-4 " onSubmit={handleSubmit((data) => sendEmail(data.email))}>
              <Label htmlFor="email">E-mail</Label>
              <Input type="email" id="email" {...register('email')} />
              <p className="text-sm text-destructive">{errors.email?.message}</p>

              <Button disabled={isPending || isSuccess} className="w-full" type="submit">
                {isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : 'Recuperar acesso'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
